/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Moment from "react-moment";
import AppLoader from "../../../components/AppLoader";
import { usePageLoader } from "../../../services/usePageLoader";
import { ENDPOINT } from "../../../services/api-endpoint";
import { getList, getListById } from "../../../services/services";
import axios from "axios";
import { APP_CONFIG } from "../../../config/config";
import type { UiOrder } from "../mes-order-utils";
import {
  MOCK_PLATFORM_ORDERS,
  normalizeManualOrder,
} from "../mes-order-utils";
import {
  mapPartnerOrderDetailPayload,
  type PartnerOrderDetailUi,
} from "../partner-order-detail-map";
import {
  mergePlatformPartWorkflowAssignments,
  type MesOrderWorkflowAssignment,
} from "../order-workflow-merge";
import { fetchManualOrderDetailUi } from "../manual-order-detail";
import { Table } from "react-bootstrap";
import "./order-detail.scss";

type OrderDetailPartRow = {
  key: string;
  part: string;
  workflow: string;
  qty: number | null;
  jobs: string[];
  done: number;
  total: number;
  /** Manual lines only: false when quantity not set in DB */
  hasQty?: boolean;
};

type ClientRow = {
  id: number;
  clientname?: string;
  companyorgname?: string;
  email?: string;
  contactphone?: string;
  city?: string;
  country?: string;
};

/** Demo parts for mock platform POs (001 / 002) — not used for real manual orders. */
const demoParts = (code: string): OrderDetailPartRow[] => [
  {
    part: "Turbo Impeller",
    workflow: "Turbo Impeller — Precision",
    qty: 25,
    jobs: ["Precision", "Batch 1"] as string[],
    done: 10,
    total: 25,
  },
  {
    part: "Piston Pin",
    workflow: "—",
    qty: 500,
    jobs: [] as string[],
    done: 0,
    total: 500,
  },
  {
    part: "Connecting Rod",
    workflow: "—",
    qty: 150,
    jobs: [] as string[],
    done: 0,
    total: 150,
  },
].map((p, i) => ({ ...p, key: `${code}-${i}`, hasQty: true }));

const demoJobs = (code: string) => [
  {
    name: "Turbo Impeller — Precision",
    status: "In Progress" as const,
    tasksDone: 2,
    tasksTotal: 4,
    recipe: "—",
  },
  {
    name: "Piston Pin — Batch 1",
    status: "Open" as const,
    tasksDone: 0,
    tasksTotal: 3,
    recipe: "—",
  },
  {
    name: "Connecting Rod — Line A",
    status: "Open" as const,
    tasksDone: 0,
    tasksTotal: 2,
    recipe: "—",
  },
].map((j, i) => ({ ...j, key: `${code}-job-${i}` }));

/** Mock PO-2024-003 — matches Platform demo UI (part names, qty, no jobs). */
const mockPo2024003Parts = (code: string): OrderDetailPartRow[] =>
  [
    {
      part: "Brake Caliper Mount",
      workflow: "—",
      qty: 80,
      jobs: [] as string[],
      done: 0,
      total: 80,
    },
    {
      part: "Flywheel",
      workflow: "—",
      qty: 40,
      jobs: [] as string[],
      done: 0,
      total: 40,
    },
  ].map((p, i) => ({ ...p, key: `${code}-${i}`, hasQty: true }));

const mockPlatformParts = (orderId: number, code: string) =>
  orderId === 900003 ? mockPo2024003Parts(code) : demoParts(code);

const mockPlatformJobs = (orderId: number, code: string) =>
  orderId === 900003 ? [] : demoJobs(code);

const OrderDetail = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, runWithLoading } = usePageLoader();
  const [order, setOrder] = useState<UiOrder | null>(null);
  const [clientRow, setClientRow] = useState<ClientRow | null>(null);
  const [partnerDetail, setPartnerDetail] = useState<PartnerOrderDetailUi | null>(
    null,
  );
  const [partnerDetailLoading, setPartnerDetailLoading] = useState(false);
  const [platformWorkflows, setPlatformWorkflows] = useState<MesOrderWorkflowAssignment[]>(
    [],
  );
  const [manualDetailLoading, setManualDetailLoading] = useState(false);
  const [manualDetail, setManualDetail] = useState<Awaited<
    ReturnType<typeof fetchManualOrderDetailUi>
  > | null>(null);

  const idNum = Number(orderId);
  const passOrder = (location.state as { order?: UiOrder })?.order;
  const hasPass = Boolean(passOrder && String(passOrder.id) === String(orderId));
  const hasMock = MOCK_PLATFORM_ORDERS.some((o) => String(o.id) === String(orderId));
  const needsApiFetch = Number.isFinite(idNum) && idNum > 0 && !hasPass && !hasMock;
  const [fetchDone, setFetchDone] = useState(() => !needsApiFetch);

  const resolveFromMocks = (id: number): UiOrder | null => {
    return MOCK_PLATFORM_ORDERS.find((o) => o.id === id) ?? null;
  };

  const loadClient = useCallback(
    async (clientId: string | number | null | undefined) => {
      if (clientId == null || clientId === "") {
        setClientRow(null);
        return;
      }
      const list = await getList(ENDPOINT.CLIENT_LIST);
      const rows = Array.isArray(list) ? list : [];
      const c = rows.find((x: ClientRow) => String(x.id) === String(clientId));
      setClientRow(c || null);
    },
    [],
  );

  useLayoutEffect(() => {
    const id = Number(orderId);
    const fromState = (location.state as { order?: UiOrder })?.order;
    if (fromState && String(fromState.id) === String(orderId)) {
      setOrder(fromState);
      if (fromState.source === "manual" && fromState.clientid != null) {
        void loadClient(fromState.clientid);
      } else {
        setClientRow({
          id: 0,
          companyorgname: fromState.clientName,
          clientname: fromState.clientName,
        });
      }
      return;
    }

    const mock = resolveFromMocks(id);
    if (mock) {
      setOrder(mock);
      setClientRow({
        id: 0,
        companyorgname: mock.clientName,
        clientname: mock.clientName,
      });
    }
  }, [orderId, location.state, loadClient]);

  useEffect(() => {
    if (!order || order.source !== "platform") {
      setPartnerDetail(null);
      setPlatformWorkflows([]);
      return;
    }
    const isMock = MOCK_PLATFORM_ORDERS.some((o) => o.id === order.id);
    if (isMock) {
      setPartnerDetail(null);
      setPlatformWorkflows([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setPartnerDetailLoading(true);
      try {
        const [raw, wfRows] = await Promise.all([
          getListById(ENDPOINT.PARTNER_VENDOR_ORDER, order.id),
          getList(`${ENDPOINT.WORKFLOWS_BY_ORDER}/${order.id}`, {
            order_source: "platform",
          }),
        ]);
        if (cancelled) return;
        if (raw == null || (Array.isArray(raw) && raw.length === 0)) {
          setPartnerDetail(null);
        } else {
          setPartnerDetail(mapPartnerOrderDetailPayload(raw));
        }
        setPlatformWorkflows(Array.isArray(wfRows) ? (wfRows as MesOrderWorkflowAssignment[]) : []);
      } catch {
        if (!cancelled) {
          setPartnerDetail(null);
          setPlatformWorkflows([]);
        }
      } finally {
        if (!cancelled) setPartnerDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [order]);

  useEffect(() => {
    if (!order || order.source !== "manual" || !Number.isFinite(order.id)) {
      setManualDetail(null);
      setManualDetailLoading(false);
      return;
    }

    let cancelled = false;
    setManualDetailLoading(true);
    void (async () => {
      try {
        const d = await fetchManualOrderDetailUi(order.id);
        if (!cancelled) setManualDetail(d);
      } catch {
        if (!cancelled) setManualDetail({ parts: [], jobs: [] });
      } finally {
        if (!cancelled) setManualDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [order]);

  useEffect(() => {
    const id = Number(orderId);
    const fromState = (location.state as { order?: UiOrder })?.order;
    if (fromState && String(fromState.id) === String(orderId)) return;
    if (resolveFromMocks(id)) return;

    if (!Number.isFinite(id) || id <= 0) {
      setOrder(null);
      setFetchDone(true);
      return;
    }

    (async () => {
      await runWithLoading(async () => {
        try {
          const REQUEST_URL = `${APP_CONFIG.API}${ENDPOINT.ORDER_LIST_BY_ID}/${id}`;
          const RESPONSE = await axios.get(REQUEST_URL);
          const payload = RESPONSE?.data;
          const row = Array.isArray(payload) ? payload[0] : payload;
          if (!row) {
            setOrder(null);
            return;
          }
          const clients = await getList(ENDPOINT.CLIENT_LIST);
          const clin = Array.isArray(clients) ? clients : [];
          const label = (cid: string | number | null | undefined) => {
            const c = clin.find((x: ClientRow) => String(x.id) === String(cid));
            return c?.companyorgname || c?.clientname || `Client #${cid}`;
          };
          const ui = normalizeManualOrder(row, label);
          setOrder(ui);
          await loadClient(row.clientid);
        } catch {
          setOrder(null);
        } finally {
          setFetchDone(true);
        }
      });
    })();
  }, [orderId, location.state, loadClient, runWithLoading]);

  if (!order && (!fetchDone || isLoading)) {
    return (
      <div>
        <AppLoader text="Loading order..." />
      </div>
    );
  }

  if (!order && fetchDone && !isLoading) {
    return (
      <div>
        <div className="tv-page-content p-4">
            <p>Order not found.</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate("/mes-order")}>
              Back to orders
            </button>
          </div>
      </div>
    );
  }

  const partnerCompany =
    order?.source === "platform" && partnerDetail?.company
      ? partnerDetail.company.trim()
      : "";
  const company =
    (partnerCompany && partnerCompany !== "—" ? partnerCompany : null) ||
    clientRow?.companyorgname ||
    clientRow?.clientname ||
    order?.clientName ||
    "—";

  const contactDisplay =
    order?.source === "platform" && partnerDetail != null
      ? partnerDetail.contact?.trim() || "—"
      : clientRow?.clientname || "—";

  const emailDisplay =
    order?.source === "platform" && partnerDetail != null
      ? partnerDetail.email?.trim() || "—"
      : clientRow?.email || "—";

  const locationDisplay =
    order?.source === "platform" && partnerDetail != null
      ? partnerDetail.location?.trim() || "—"
      : [clientRow?.city, clientRow?.country].filter(Boolean).join(", ") || "—";

  const metaCreated =
    order?.source === "platform" && partnerDetail?.issuedAt != null
      ? partnerDetail.issuedAt
      : order?.createdate;
  const metaDue =
    order?.source === "platform" && partnerDetail?.dispatchDate != null
      ? partnerDetail.dispatchDate
      : order?.duedate;

  const isMockPlatform =
    order?.source === "platform" &&
    MOCK_PLATFORM_ORDERS.some((o) => o.id === order.id);

  const parts: OrderDetailPartRow[] =
    isMockPlatform
      ? mockPlatformParts(order?.id ?? 0, order?.ordercode || "")
      : order?.source === "platform" && partnerDetail?.partLines?.length
        ? mergePlatformPartWorkflowAssignments(
            partnerDetail.partLines,
            platformWorkflows,
          ).map((p) => ({
            key: p.key,
            part: p.part,
            workflow: p.workflow,
            qty: p.qty,
            jobs: p.jobTags,
            done: p.done,
            total: Math.max(p.total, 1),
            hasQty: true,
          }))
        : order?.source === "platform"
          ? []
          : order?.source === "manual"
            ? (manualDetail?.parts ?? []).map((p) => ({
                key: p.key,
                part: p.part,
                workflow: p.workflow,
                qty: p.qty,
                jobs: p.jobTags,
                done: p.done,
                total: p.total,
                hasQty: p.hasQty,
              }))
            : [];

  const jobs =
    isMockPlatform
      ? mockPlatformJobs(order?.id ?? 0, order?.ordercode || "")
      : order?.source === "platform" && partnerDetail?.jobs?.length
        ? partnerDetail.jobs.map((j) => ({
            key: j.key,
            name: j.name,
            status: j.status,
            tasksDone: j.tasksDone,
            tasksTotal: Math.max(j.tasksTotal, 1),
            recipe: j.recipe,
          }))
        : order?.source === "platform"
          ? []
          : order?.source === "manual"
            ? (manualDetail?.jobs ?? []).map((j) => ({
                key: j.key,
                name: j.name,
                status: j.status,
                tasksDone: j.tasksDone,
                tasksTotal: Math.max(j.tasksTotal, 1),
                recipe: j.recipe,
              }))
            : [];

  const showJobsSection =
    isMockPlatform ||
    order?.source === "manual" ||
    (order?.source === "platform" && Boolean(partnerDetail?.jobs?.length));

  const showPartsEmpty =
    order != null &&
    parts.length === 0 &&
    (order.source === "manual"
      ? !manualDetailLoading
      : order.source === "platform" && !partnerDetailLoading && !isMockPlatform);

  const statusBadge = (s: string) => {
    if (s === "In Progress") return "tv-od-pill tv-od-pill--progress";
    if (s === "Open") return "tv-od-pill tv-od-pill--open";
    return "tv-od-pill tv-od-pill--draft";
  };

  return (
    <div>
      {order && (
        <div className="tv-page-content tv-od-page">
            <button
              type="button"
              className="btn btn-link tv-od-back pl-0"
              onClick={() => navigate("/mes-order")}
            >
              ← Back to Orders
            </button>

            <div className="tv-od-head">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h1 className="tv-od-title">{order.ordercode}</h1>
                <span className={statusBadge(order.status)}>{order.status}</span>
                {order.source === "manual" ? (
                  <span className="tv-pill tv-pill--source-manual">
                    <i className="fa fa-upload mr-1" aria-hidden="true" />
                    Manual Order
                  </span>
                ) : (
                  <span className="tv-pill tv-pill--source-platform">
                    <i className="fa fa-cube mr-1" aria-hidden="true" />
                    Platform Order
                  </span>
                )}
              </div>
              <p className="tv-od-meta text-muted">
                {company} —{" "}
                <Moment format="D MMM YYYY">{metaCreated}</Moment>
                {" — "}
                {metaDue ? (
                  <Moment format="D MMM YYYY">{metaDue}</Moment>
                ) : (
                  "—"
                )}
              </p>
            </div>

            {order.source === "manual" && (
              <div className="tv-od-banner tv-od-banner--manual">
                <i className="fa fa-upload tv-od-banner-icon" aria-hidden="true" />
                <div className="flex-grow-1">
                  <div className="tv-od-banner-title">Manual Order — CAD Upload Required</div>
                  <p className="tv-od-banner-text mb-0">
                    This order was entered manually from outside the Truventor platform. CAD files
                    need to be uploaded to generate CAM operations and inspection plans.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary tv-od-banner-btn"
                  onClick={() =>
                    navigate("/mes-order/cad-manufacturing", { state: { order } })
                  }
                >
                  <i className="fa fa-upload mr-2" aria-hidden="true" />
                  Upload CAD &amp; Generate CAM
                </button>
              </div>
            )}

            {order.source === "platform" && (
              <div className="tv-od-banner tv-od-banner--platform">
                <i className="fa fa-cube tv-od-banner-icon tv-od-banner-icon--platform" aria-hidden="true" />
                <div className="flex-grow-1">
                  <div className="tv-od-banner-title">Platform Order — CAD Auto-Processed</div>
                  <p className="tv-od-banner-text mb-2">
                    CAD files and manufacturing plans for this order were generated automatically
                    through the Truventor pipeline.
                  </p>
                  <ul className="tv-od-platform-steps list-unstyled mb-0">
                    {[
                      "CAD file received",
                      "Features extracted",
                      "CAM generated",
                      "Inspection plan ready",
                    ].map((label) => (
                      <li key={label} className="tv-od-platform-step">
                        <i className="fa fa-check tv-od-platform-step-icon" aria-hidden="true" />
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="tv-od-card">
              <div className="tv-od-section-label">Client</div>
              <div className="tv-od-client-grid">
                <div>
                  <span className="tv-od-field-label">Company</span>
                  <div className="tv-od-field-val">{company}</div>
                </div>
                <div>
                  <span className="tv-od-field-label">Contact</span>
                  <div className="tv-od-field-val">{contactDisplay}</div>
                </div>
                <div>
                  <span className="tv-od-field-label">Email</span>
                  <div className="tv-od-field-val">{emailDisplay || "—"}</div>
                </div>
                <div>
                  <span className="tv-od-field-label">Location</span>
                  <div className="tv-od-field-val">{locationDisplay || "—"}</div>
                </div>
              </div>
            </div>

            <div className="tv-od-card">
              <div className="tv-od-card-head-row">
                <span className="tv-od-section-label mb-0">Order parts</span>
                <span className="text-muted small">
                  {(partnerDetailLoading && order?.source === "platform") ||
                  (manualDetailLoading && order?.source === "manual")
                    ? "Loading…"
                    : `${parts.length} parts`}
                </span>
              </div>
              <Table responsive borderless className="tv-od-table mb-0">
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Workflow template</th>
                    <th>Quantity</th>
                    <th>Jobs</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {showPartsEmpty ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No parts linked to this order yet.
                      </td>
                    </tr>
                  ) : (
                    parts.map((p) => {
                      const hideQty = p.hasQty === false || p.qty == null;
                      return (
                        <tr key={p.key}>
                          <td className="font-weight-bold">{p.part}</td>
                          <td className={p.workflow === "—" ? "text-muted font-italic" : ""}>
                            {p.workflow === "—" ? "Not assigned" : p.workflow}
                          </td>
                          <td>{hideQty ? "—" : p.qty}</td>
                          <td>
                            {p.jobs.length ? (
                              p.jobs.map((j) => (
                                <span key={j} className="tv-od-job-chip mr-1">
                                  {j}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted font-italic">No jobs yet</span>
                            )}
                          </td>
                          <td style={{ minWidth: 180 }}>
                            {hideQty ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <div className="tv-od-progress-row">
                                <div className="tv-od-progress tv-od-progress--green">
                                  <div
                                    className="tv-od-progress-fill"
                                    style={{
                                      width: `${Math.min(
                                        100,
                                        (p.done / Math.max(p.total, 1)) * 100,
                                      )}%`,
                                    }}
                                  />
                                </div>
                                <span className="tv-od-progress-fraction">
                                  {p.done}/{p.total}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            {showJobsSection && (
              <div className="tv-od-card">
                <div className="tv-od-card-head-row">
                  <span className="tv-od-section-label mb-0">
                    Jobs from this order
                  </span>
                  <span className="text-muted small">{jobs.length} jobs</span>
                </div>
                <Table responsive borderless className="tv-od-table mb-0">
                  <thead>
                    <tr>
                      <th>Job name</th>
                      <th>Status</th>
                      <th>Tasks progress</th>
                      <th>Recipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="tv-od-jobs-empty text-center text-muted py-4">
                          No jobs created yet for this order
                        </td>
                      </tr>
                    ) : (
                      jobs.map((j) => (
                        <tr key={j.key}>
                          <td>{j.name}</td>
                          <td>
                            <span className={statusBadge(j.status)}>{j.status}</span>
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <div className="tv-od-progress-row">
                              <div className="tv-od-progress tv-od-progress--purple">
                                <div
                                  className="tv-od-progress-fill"
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      j.tasksTotal
                                        ? (j.tasksDone / j.tasksTotal) * 100
                                        : 0,
                                    )}%`,
                                  }}
                                />
                              </div>
                              <span className="tv-od-progress-fraction">
                                {j.tasksDone}/{j.tasksTotal}
                              </span>
                            </div>
                          </td>
                          <td
                            className={
                              j.recipe === "No recipe" || j.recipe === "—"
                                ? "text-muted font-italic"
                                : ""
                            }
                          >
                            {j.recipe}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default OrderDetail;
